import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Tooltip, Popover, Select } from 'antd';

import Avatars from '../../atoms/side-navbar/GroupAvatar';
import {
  getParticipantsSelectOptions,
  hasParticipantsSelector,
} from '../../../../redux/projects/selectors';
import {
  getUserReducerUsersSelectOptions,
  getWorkspaceMembersSelectOptions,
  hasUserReducerUsersSelector,
} from '../../../../redux/general/selectors';
import { makeGetTaskAssignees } from '../../../../redux/tasks/taskEntities/selectors';
import { editTaskAction } from '../../../../redux/tasks/actions';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import { toggleProjectInvitesBanner } from '../../../../actions/modalAction';
import { closeContextMenu } from '../../../../js/functions';
import useCloseOnScroll from '../../../../hooks/useCloseOnScroll';

const defaultFn = () => {};

const ConnectedAssigneeSelect = ({
  count,
  iconSize,
  selectOptions,
  showNames,
  taskAssignees,
  taskId,
  workspaceMembers,
  disableDeselect,
  me,
  callback,
  currentValue,
  setCurrentValue,
  onClose,
  onOpen,
  isAddAssignee = false,
  readonly,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { 1: selectedProjectId } = window.location.pathname.split('/');

  const [assigneeValue, setAssigneeValue] = useState([]);
  const [projectInvites, setProjectInvites] = useState([]);
  const [options, setOptions] = useState(workspaceMembers);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const selectRef = useRef(null);
  const unassignedRef = useRef(null);
  const triggerRef = useRef(null);

  useCloseOnScroll(popoverOpen, triggerRef, () => setPopoverOpen(false));

  const selectedValue = currentValue || assigneeValue;

  const setValue = useCallback((value) => {
    if (typeof setCurrentValue === 'function') {
      setCurrentValue?.(value);
    } else {
      setAssigneeValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const x = [];
    if (!isAddAssignee) {
      taskAssignees?.forEach((assignee) => {
        if (assignee?.assigneeId?._id) {
          x.push({
            value: assignee.assigneeId?._id,
            label: assignee.assigneeId?.fullName,
            email: assignee.assigneeId?.email,
            avi: assignee.assigneeId?.avi,
          });
        }
      });
      setValue(x);
    }
  }, [taskAssignees]);

  useEffect(() => {
    setOptions(workspaceMembers);
  }, [workspaceMembers]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  const handleSubmit = useCallback(
    (optional) => {
      let users = [];
      const assignees = optional || selectedValue;
      // assignees.forEach((user) => {
      //   users = [...users, { assigneeId: user.value }];
      // });

      if (callback) {
        callback(selectedValue || []);
        return;
      }

      let updateNeeded = false;
      assignees.forEach((user) => {
        if (
          !taskAssignees?.find(
            (a) => a?.assigneeId?._id?.toString() === user?.value,
          )
        ) {
          updateNeeded = true;
        }
        users = [
          ...users,
          {
            assigneeId: {
              _id: user.value,
              email: user.email,
              fullName: user.label,
              avi: user.avi,
            },
          },
        ];
      });

      if (users?.length === 0 || users?.length !== taskAssignees?.length) {
        updateNeeded = true;
      }

      if (updateNeeded) {
        dispatch(
          editTaskAction(
            {
              _id: taskId,
              assignee: users,
            },
            null,
          ),
        );
      }

      // check if they in project, otherwise invite them
      if (projectInvites?.length > 0 && selectedProjectId) {
        dispatch(toggleProjectInvitesBanner(selectedProjectId, projectInvites));
      }
    },
    [selectedValue, taskAssignees, taskId],
  );

  const hasAssignees = selectedValue?.length !== 0;

  const unassign = useCallback(() => setValue([]), []);
  const ids = selectOptions?.map((option) => option?.value);

  const optionRefs = useRef([]);
  // Always use orderedOptions for highlight/scroll
  const getOrderedOptions = useCallback(() => {
    const meOption = options.find((opt) => opt?.value === me?._id);
    const otherOptions = options.filter((opt) => opt?.value !== me?._id);
    return meOption ? [meOption, ...otherOptions] : otherOptions;
  }, [options, me]);

  const orderedOptions = getOrderedOptions();

  useEffect(() => {
    if (highlightedIndex === -1) {
      unassignedRef.current?.scrollIntoView({ block: 'nearest' });
    } else {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedIndex, orderedOptions]);

  const UserOption = ({ option, index }) => {
    if (!option?.value) {
      return null;
    }

    const isSelected = selectedValue.some(
      (user) => user?.value === option?.value,
    );
    const isHighlighted = index === highlightedIndex;
    return (
      <div
        key={option?.email}
        ref={(el) => (optionRefs.current[index] = el)}
        onClick={
          readonly
            ? defaultFn
            : (e) => {
                e.stopPropagation();
                setOptions(workspaceMembers);
                setSearch('');
                setHighlightedIndex(index);
                selectRef.current?.focus();
                const isMemberNotInProject = !ids?.includes(option?.value);
                if (!isSelected) {
                  setValue([...selectedValue, option]);
                  setSearch('');
                  setOptions(workspaceMembers);
                  isMemberNotInProject &&
                    setProjectInvites((prev) => [...prev, option?.value]);
                } else if (!disableDeselect) {
                  setValue(
                    selectedValue.filter(
                      (user) => user?.value !== option?.value,
                    ),
                  );
                  setProjectInvites(
                    projectInvites.filter(
                      (user) => user?.value !== option?.value,
                    ),
                  );
                  setHighlightedIndex(-1);
                }
              }
        }
        className={`flex align-center pl-2 py-2 my-1 hover:bg-slate-200 w-full cursor-pointer ${isHighlighted ? 'bg-blue-100 text-blue-900' : isSelected ? 'bg-blue-100' : ''}`}
        tabIndex={-1}
      >
        <UserAvatar
          alt="Avatar"
          classNames="card-image object-cover h-10 w-10 shrink-0"
          fullName={option?.label}
          userId={option?.value}
          src={option?.avi}
          styles={{
            width: '40px',
            height: '40px',
            aspectRatio: 1,
          }}
        />
        <div className="ml-3 flex justify-between align-center w-full">
          <div>
            <div className="font-16 font-bold">{option?.label}</div>
            <div className="font-12">{option?.email}</div>
          </div>
          {!readonly && isSelected && (
            <svg
              className={`w-7 h-7 fill-current text-blue-500 shrink-0 mr-2`}
              viewBox="0 0 16 16"
            >
              <path d="m2.457 8.516.969-.99 2.516 2.481 5.324-5.304.985.989-6.309 6.284z" />
            </svg>
          )}
        </div>
      </div>
    );
  };

  const dropdownRenderer = useCallback(
    () => (
      <div
        className={`assignee-dropdown-content-div w-[300px]`}
        tabIndex={0}
        onKeyDown={(e) => {
          let idx = highlightedIndex;
          switch (e.key) {
            case 'ArrowDown':
              setHighlightedIndex(Math.min(idx + 1, orderedOptions.length - 1));
              break;
            case 'ArrowUp':
              setHighlightedIndex(idx <= 0 ? -1 : idx - 1);
              break;
            case 'Enter': {
              if (idx === -1) {
                setOptions(workspaceMembers);
                setSearch('');
                setValue([]);
                unassign();
                setHighlightedIndex(-1);
              } else {
                const opt = orderedOptions[idx];
                if (opt) {
                  const exists = selectedValue.some(
                    (u) => u?.value === opt?.value,
                  );
                  setValue(
                    exists
                      ? selectedValue.filter((u) => u?.value !== opt?.value)
                      : [...selectedValue, opt],
                  );
                  setSearch('');
                  setOptions(workspaceMembers);
                  selectRef.current?.blur();
                  setTimeout(() => selectRef.current?.focus(), 0);
                }
              }
              break;
            }
            default:
              return;
          }
          e.preventDefault();
        }}
      >
        <div className="dropdown-content overflow-hidden">
          <div className="p-3">
            <Select
              ref={selectRef}
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Search project members..."
              dropdownRender={() => null} // completely hide the dropdown
              suffixIcon={null} // hides the dropdown icon
              onClick={(e) => e.stopPropagation()}
              onChange={(input) => {
                let users = [];

                selectedValue.map((user) => {
                  if (input.includes(user?.value)) {
                    users.push(user);
                  }
                  return;
                });

                setValue(users);
                setOptions(workspaceMembers);
                if (!input || (Array.isArray(input) && input.length === 0)) {
                  setHighlightedIndex(-1);
                  setSearch('');
                }
              }}
              onSearch={(input) => {
                const projectAssignee = workspaceMembers?.filter((opt) => {
                  const label = opt?.label?.toLowerCase();
                  return label?.includes(input?.toLowerCase());
                });

                setOptions(input ? projectAssignee : workspaceMembers);
                setSearch(input);
                setHighlightedIndex(-1);
              }}
              options={orderedOptions}
              value={selectedValue}
              autoFocus
            />
            <div className="overflow-y-scroll w-full max-h-80 mt-2">
              {orderedOptions.length === 0 ? (
                <div className="flex align-center justify-center bg-slate-100 p-2">
                  No matches found
                </div>
              ) : (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptions(workspaceMembers);
                    setSearch('');
                    setValue([]);
                    unassign();
                    setHighlightedIndex(-1);
                  }}
                  ref={unassignedRef}
                  className={`flex align-center pl-2 py-2 w-full cursor-pointer ${
                    highlightedIndex === -1
                      ? 'bg-slate-200 text-blue-900'
                      : 'hover:bg-slate-200 text-black'
                  }`}
                >
                  <div className="card-image bg-indigo-200 h-10 w-10 flex align-center justify-center font-16">
                    {search ? 'X' : 'U'}
                  </div>
                  <div className="ml-3">
                    <div className="font-16 font-bold">
                      {search ? 'Clear' : 'Unassigned'}
                    </div>
                  </div>
                </div>
              )}
              {orderedOptions.map((option, i) => (
                <UserOption
                  option={option}
                  index={i}
                  key={option?.value || i}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
    [selectedValue, orderedOptions, unassign, highlightedIndex],
  );

  return (
    <div ref={triggerRef} className="assignee-popover">
      <Popover
        onClick={(e) => e.stopPropagation()}
        content={readonly ? defaultFn : dropdownRenderer}
        placement="bottom"
        open={popoverOpen}
        onOpenChange={
          readonly
            ? defaultFn
            : (open) => {
                setPopoverOpen(open);
                if (open) {
                  if (closeContextMenu) {
                    closeContextMenu();
                  }
                }
                setProjectInvites([]);
                if (open) {
                  setHighlightedIndex(-1);
                  setTimeout(() => {
                    if (selectRef.current && selectRef.current.focus) {
                      selectRef.current.focus();
                    }
                  }, 0);
                }
                if (!open) {
                  handleSubmit();
                  onClose?.();
                }
              }
        }
        trigger="click"
      >
        <>
          {hasAssignees ? (
            <a>{/* <Space>add assignee</Space> */}</a>
          ) : readonly ? null : (
            <div className="flex align-center cursor-pointer">
              <Tooltip title="Add assignee">
                <div
                  className={`add-assignee-btn-wrapper flex align-center ${showNames ? '-ml-1' : 'ml-2'}  items-center`}
                >
                  <button
                    type="button"
                    className=" flex align-center justify-center shrink-0 rounded-full  border-slate-100 size-5 transition duration-150"
                  >
                    <img
                      src={'/images/icons/blue-round-add.svg'}
                      alt="add"
                      className="size-5"
                    />
                  </button>
                  {showNames && (
                    <div className="font-14 ml-2 text-[#002337] hover:text-blue-500">
                      Add Assignee
                    </div>
                  )}
                </div>
              </Tooltip>
            </div>
          )}
          {hasAssignees && (
            <div className="cursor-pointer">
              <Avatars
                participants={selectedValue}
                size={iconSize}
                showNames={showNames}
                count={count}
                children={
                  showNames &&
                  hasAssignees && (
                    <div className="m-2 flex cursor-pointer">
                      <Tooltip title="Add another assignee">
                        <div className="flex align-center -ml-1 items-center">
                          <button
                            type="button"
                            className=" flex align-center justify-center shrink-0 rounded-full  border-slate-100 size-5 transition duration-150"
                          >
                            <img
                              src={'/images/icons/blue-round-add.svg'}
                              alt="add"
                              className="size-5"
                            />
                          </button>
                          <div className="font-14 ml-2 text-[#002337] hover:text-blue-500">
                            Add assignee
                          </div>
                        </div>
                      </Tooltip>
                    </div>
                  )
                }
              />
            </div>
          )}
        </>
      </Popover>
    </div>
  );
};

const makeMapStateToProps = () => {
  const getTaskAssignees = makeGetTaskAssignees();
  return (state, props) => {
    const workspaceMembers = getWorkspaceMembersSelectOptions(state);
    if (props?.readonly) {
      return {
        taskAssignees: props?.taskAssignees
          ?.map((assignee) => {
            const id = assignee?.assigneeId;
            const member = workspaceMembers?.find(
              (member) => member?.value === id,
            );

            if (!member) {
              return false;
            }

            return {
              assigneeId: {
                _id: id,
                fullName: member?.label,
                email: member?.email,
                avi: member?.avi,
              },
            };
          })
          ?.filter(Boolean),
        workspaceMembers,
        workspaceId: state.workspaceReducer.currentWorkspace?._id,
        me: state.userReducer.user,
      };
    }
    const hasParticipants = hasParticipantsSelector(state);
    const hasUsers = hasUserReducerUsersSelector(state);
    return {
      taskAssignees: getTaskAssignees(state, props) || props.assignees || [],
      selectOptions: hasParticipants
        ? getParticipantsSelectOptions(state)
        : hasUsers
          ? getUserReducerUsersSelectOptions(state)
          : getWorkspaceMembersSelectOptions(state),
      workspaceMembers,
      workspaceId: state.workspaceReducer.currentWorkspace?._id,
      me: state.userReducer.user,
    };
  };
};

export default connect(makeMapStateToProps)(ConnectedAssigneeSelect);
